import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import PostingInstructions from '../components/PostingInstructions.jsx';
// The newsletter (PDF, email code, instructions) lived beside the package
// here from Wave 13 to session 53. Leena, 2026-10-14: "Newsletter should only
// sit under Newsletter", so this page keeps to the post: View live, Download
// package, How to put this on your website. See pages/Newsletters.jsx.
import { clientPath } from '../lib/clientLink.js';
import StatusChip from '../components/StatusChip.jsx';
import { fmtDay } from '../components/ApprovalBits.jsx';
import SectionHeading from '../components/HowItWorksPanel.jsx';

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

async function signedUrl(bucket, path, ttl = 300) {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, ttl);
  if (error) throw error;
  return data.signedUrl;
}

async function downloadPackage(row) {
  const url = await signedUrl('post-packages', row.package_path);
  window.location.href = url;
}

export default function Blogs() {
  const { activeId, activeClient, isAgency , me } = useAuth();
  const [rows,   setRows]  = useState(null);
  const [error,  setError] = useState(null);
  const [adhoc,  setAdhoc] = useState([]);     // Session 16: agency only

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRows(null); setError(null);
      if (!activeId) return;
      const { data, error } = await supabase
        .from('v_portal_posts')
        .select('*')
        .eq('client_id', activeId)
        .order('publish_date', { ascending: false, nullsFirst: false });
      if (cancelled) return;
      if (error) setError(error.message);
      else       setRows(data || []);
      // Session 16: ad hoc blogs, agency only (the view returns nothing for a client)
      if (isAgency) {
        const { data: ad } = await supabase.from('v_portal_adhoc')
          .select('job_id, client_id, title, status, package_path, package_ready, updated_at')
          .eq('client_id', activeId).order('updated_at', { ascending: false });
        if (!cancelled) setAdhoc(ad || []);
      } else {
        setAdhoc([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, isAgency]);

  if (rows === null && !error) return <p className="lede">Loading your blogs…</p>;

  // Session 8 (B4): a parked / running publish ('publishing') stays listed
  // under Ready with a neutral "Publishing…" chip and no buttons.
  const ready = (rows || []).filter((r) => r.status === 'ready' || r.status === 'publishing');
  const published = (rows || []).filter((r) => r.status === 'published' || r.status === 'delivered');

  return (
    <>
      <SectionHeading
        title="Your blogs"
        explainer={me?.approval_mode === 'client_deadline' ? 'blogs_auto' : 'blogs'}
        label="your blogs" />
      <p className="lede">
        Every finished post for {activeClient?.name || 'your business'}. Read
        one, download the package, or preview what's coming next.
      </p>
      {/* Wave 13, decision 14 and Standing Decisions 2: a locked service is
          shown as an opportunity, not hidden. For a client without the
          newsletter; the agency sees the real switch in client settings. */}
      {!isAgency && me && !(me.entitlements || []).includes('newsletter') && (
        <p className="hint">
          <span className="mode-tag">Newsletter</span> A newsletter made from every post, ready to
          send to your list. Not part of your plan yet.{' '}
          <Link to={clientPath('/learn/email', activeId)}>Learn more</Link>
        </p>
      )}

      {error && <div className="empty"><h2>Couldn't load blogs</h2><p>{error}</p></div>}

      {!error && rows && rows.length === 0 && adhoc.length === 0 && (
        <div className="empty">
          <h2>No blogs here yet</h2>
          <p>Once the first one's approved, it'll show up here.</p>
        </div>
      )}

      {ready.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Ready</h2>
            <span className="count">{ready.length} scheduled</span>
          </div>
          <div className="card">
            {ready.map((r) => (
              <BlogRow key={`ready-${r.slug}`} row={r} kind="ready" onDownload={downloadPackage} platform={activeClient?.platform} />
            ))}
          </div>
        </>
      )}

      {published.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Published</h2>
            <span className="count">{published.length} {published.some((r) => r.status === 'delivered') ? 'finished' : 'live'}</span>
          </div>
          <div className="card">
            {published.map((r) => (
              <BlogRow key={`pub-${r.slug}`} row={r} kind="published" onDownload={downloadPackage} platform={activeClient?.platform} />
            ))}
          </div>
        </>
      )}

      {isAgency && adhoc.length > 0 && (
        <>
          <div className="section-heading">
            <h2>Ad hoc</h2>
            <span className="count">{adhoc.length} · agency only</span>
          </div>
          <div className="card">
            {adhoc.map((r) => (
              <div className="blog-row" key={r.job_id}>
                <div className="title">
                  <Link to={clientPath(`/admin/clients/${r.client_id}/adhoc/${r.job_id}`, r.client_id)}>{r.title}</Link>
                  <span className="sub"><span className="mode-tag">Ad hoc</span> {r.status === 'done' ? 'Package ready' : r.status.replace('_', ' ')}</span>
                </div>
                <div className="date">{formatDate(String(r.updated_at || '').slice(0, 10))}</div>
                <div className="actions">
                  {r.package_ready && (
                    <>
                      <button className="btn btn-primary" onClick={() => downloadPackage(r).catch((e) => alert(e.message))}>Download package</button>
                      <PostingInstructions platform={activeClient?.platform} />
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

function BlogRow({ row, kind, onDownload, platform }) {
  const isReady = kind === 'ready';
  const isDelivered = row.status === 'delivered';
  const isPublishing = row.status === 'publishing';
  if (isPublishing) {
    // Session 8 (B4): neutral in-progress row. No action button, no live link yet.
    return (
      <div className="blog-row blog-row-publishing">
        <div className="title">
          <Link to={clientPath(`/blogs/${row.slug}`, row.client_id)}>{row.title || row.slug}</Link>
          <span className="sub">Publishing now{row.publish_date ? ` · scheduled ${formatDate(row.publish_date)}` : ''}</span>
        </div>
        <div className="date" />
        <div className="actions">
          <StatusChip status="publishing" />
        </div>
      </div>
    );
  }
  return (
    <div className="blog-row">
      <div className="title">
        <Link to={clientPath(`/blogs/${row.slug}`, row.client_id)}>{row.title || row.slug}</Link>
        {isReady && (
          <span className="sub">Publishes {formatDate(row.publish_date)}
            {row.auto_approved_at ? ` · Approved automatically on ${fmtDay(row.auto_approved_at)}` : ''}</span>
        )}
        {isDelivered && (
          <span className="sub">Ready to post · delivered {formatDate(String(row.published_at || '').slice(0, 10))}</span>
        )}
      </div>
      <div className="date">
        {!isReady && formatDate(row.publish_date)}
      </div>
      <div className="actions">
        {!isReady && row.live_url && (
          <a className="link-btn" href={row.live_url} target="_blank" rel="noopener noreferrer">View live</a>
        )}
        {isReady && (
          <Link className="link-btn" to={clientPath(`/blogs/${row.slug}`, row.client_id)}>Preview</Link>
        )}
        {row.package_ready ? (
          <>
            <button className="btn btn-primary" onClick={() => onDownload(row).catch((e) => alert(e.message))}>
              Download package
            </button>
            {/* Session 42: next to the download, because the moment somebody is
                about to download it is the moment they want to know what to do
                with it. A text file inside the zip is not enough on its own —
                they have to download, unzip and notice it first. */}
            <PostingInstructions platform={platform} />
          </>
        ) : (
          <span className="hint" style={{ margin: 0 }}>Package being prepared</span>
        )}
      </div>
    </div>
  );
}
